/**
 * DocumentUploader Component
 *
 * Handles file upload with drag-and-drop support, progress indication,
 * file type validation, and metadata input (name and classification).
 *
 * Requirements: 2.2, 2.3
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  ArrowUpload24Regular,
  Document24Regular,
  Dismiss20Regular,
  Warning20Regular,
  Checkmark20Regular,
  DocumentPdf24Regular,
  DocumentText24Regular,
} from '@fluentui/react-icons';
import type { DocumentClassification, DocumentMetadata, UploadedDocument } from '../../types';
import styles from './DocumentUploader.module.css';

// ============================================================================
// Constants
// ============================================================================

/** Accepted file types for upload */
const ACCEPTED_FILE_TYPES = ['.pdf', '.docx', '.doc'];

/** MIME types corresponding to accepted file types */
const ACCEPTED_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
];

/** Default maximum file size in MB */
const DEFAULT_MAX_SIZE_MB = 50;

/** Classification options for documents */
const CLASSIFICATION_OPTIONS: { value: DocumentClassification; label: string }[] = [
  { value: 'operational_manual', label: 'Operational Manual' },
  { value: 'concept_of_operations', label: 'Concept of Operations (ConOps)' },
  { value: 'other', label: 'Other' },
];

// ============================================================================
// Types
// ============================================================================

export interface DocumentUploaderProps {
  /** Callback when upload completes successfully */
  onUploadComplete: (document: UploadedDocument) => void;
  /** Callback when upload fails */
  onUploadError: (error: Error) => void;
  /** Accepted file types (default: ['.pdf', '.docx', '.doc']) */
  acceptedTypes?: string[];
  /** Maximum file size in MB (default: 50) */
  maxSizeMB?: number;
  /** Whether the uploader is disabled */
  disabled?: boolean;
  /** Custom upload handler (for integration with context) */
  uploadHandler?: (file: File, metadata: DocumentMetadata) => Promise<UploadedDocument>;
}

interface UploadState {
  status: 'idle' | 'selected' | 'uploading' | 'success' | 'error';
  progress: number;
  error: string | null;
}

interface FormState {
  name: string;
  classification: DocumentClassification | '';
}

interface ValidationErrors {
  name?: string;
  classification?: string;
  file?: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Validates a file against accepted types and size limits
 */
function validateFile(
  file: File,
  acceptedTypes: string[],
  maxSizeMB: number
): { valid: boolean; error?: string } {
  // Check file extension
  const fileName = file.name.toLowerCase();
  const hasValidExtension = acceptedTypes.some((ext) =>
    fileName.endsWith(ext.toLowerCase())
  );

  if (!hasValidExtension) {
    return {
      valid: false,
      error: `Invalid file type. Accepted types: ${acceptedTypes.join(', ')}`,
    };
  }

  // Check MIME type
  const hasValidMimeType = ACCEPTED_MIME_TYPES.includes(file.type);
  if (!hasValidMimeType && file.type !== '') {
    return {
      valid: false,
      error: `Invalid file type. Accepted types: ${acceptedTypes.join(', ')}`,
    };
  }

  // Check file size
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return {
      valid: false,
      error: `File size exceeds ${maxSizeMB}MB limit. Please choose a smaller file.`,
    };
  }

  return { valid: true };
}

/**
 * Formats file size for display
 */
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Gets the appropriate icon for a file type
 */
function getFileIcon(fileName: string): React.ReactNode {
  const ext = fileName.toLowerCase().split('.').pop();
  if (ext === 'pdf') {
    return <DocumentPdf24Regular className={styles.fileIcon} />;
  }
  return <DocumentText24Regular className={styles.fileIcon} />;
}

// ============================================================================
// Component
// ============================================================================

/**
 * DocumentUploader provides a drag-and-drop interface for uploading documents
 * with metadata input (name and classification). It validates file types and
 * sizes, shows upload progress, and handles errors gracefully.
 *
 * @example
 * // Basic usage
 * <DocumentUploader
 *   onUploadComplete={(doc) => console.log('Uploaded:', doc)}
 *   onUploadError={(err) => console.error('Error:', err)}
 * />
 *
 * @example
 * // With custom upload handler
 * <DocumentUploader
 *   onUploadComplete={(doc) => handleSuccess(doc)}
 *   onUploadError={(err) => handleError(err)}
 *   uploadHandler={async (file, metadata) => {
 *     return await uploadToServer(file, metadata);
 *   }}
 * />
 */
export const DocumentUploader: React.FC<DocumentUploaderProps> = ({
  onUploadComplete,
  onUploadError,
  acceptedTypes = ACCEPTED_FILE_TYPES,
  maxSizeMB = DEFAULT_MAX_SIZE_MB,
  disabled = false,
  uploadHandler,
}) => {
  // State
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadState, setUploadState] = useState<UploadState>({
    status: 'idle',
    progress: 0,
    error: null,
  });
  const [formState, setFormState] = useState<FormState>({
    name: '',
    classification: '',
  });
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [isDragOver, setIsDragOver] = useState(false);

  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  // Reset form when file is cleared
  useEffect(() => {
    if (!selectedFile) {
      setFormState({ name: '', classification: '' });
      setValidationErrors({});
      setUploadState({ status: 'idle', progress: 0, error: null });
    }
  }, [selectedFile]);

  /**
   * Handles file selection from input or drop
   */
  const handleFileSelect = useCallback(
    (file: File) => {
      const validation = validateFile(file, acceptedTypes, maxSizeMB);

      if (!validation.valid) {
        setValidationErrors({ file: validation.error });
        setUploadState({
          status: 'error',
          progress: 0,
          error: validation.error || 'Invalid file',
        });
        return;
      }

      setSelectedFile(file);
      setValidationErrors({});
      setUploadState({ status: 'selected', progress: 0, error: null });

      // Pre-fill name from filename (without extension)
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
      setFormState((prev) => ({
        ...prev,
        name: nameWithoutExt,
      }));
    },
    [acceptedTypes, maxSizeMB]
  );

  /**
   * Handles file input change
   */
  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
      // Reset input value to allow selecting the same file again
      event.target.value = '';
    },
    [handleFileSelect]
  );

  /**
   * Handles drag over event
   */
  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (!disabled) {
        setIsDragOver(true);
      }
    },
    [disabled]
  );

  /**
   * Handles drag leave event
   */
  const handleDragLeave = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      // Only set drag over to false if leaving the drop zone entirely
      if (
        dropZoneRef.current &&
        !dropZoneRef.current.contains(event.relatedTarget as Node)
      ) {
        setIsDragOver(false);
      }
    },
    []
  );

  /**
   * Handles drop event
   */
  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(false);

      if (disabled) return;

      const file = event.dataTransfer.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [disabled, handleFileSelect]
  );

  /**
   * Opens file picker dialog
   */
  const handleBrowseClick = useCallback(() => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  }, [disabled]);

  /**
   * Clears the selected file
   */
  const handleClearFile = useCallback(() => {
    setSelectedFile(null);
  }, []);

  /**
   * Handles form field changes
   */
  const handleFormChange = useCallback(
    (field: keyof FormState, value: string) => {
      setFormState((prev) => ({ ...prev, [field]: value }));
      // Clear validation error for this field
      setValidationErrors((prev) => ({ ...prev, [field]: undefined }));
    },
    []
  );

  /**
   * Validates the form
   */
  const validateForm = useCallback((): boolean => {
    const errors: ValidationErrors = {};

    if (!formState.name.trim()) {
      errors.name = 'Document name is required';
    }

    if (!formState.classification) {
      errors.classification = 'Classification is required';
    }

    if (!selectedFile) {
      errors.file = 'Please select a file to upload';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formState, selectedFile]);

  /**
   * Handles form submission
   */
  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();

      if (!validateForm() || !selectedFile) {
        return;
      }

      setUploadState({ status: 'uploading', progress: 0, error: null });

      try {
        // Simulate progress updates
        const progressInterval = setInterval(() => {
          setUploadState((prev) => ({
            ...prev,
            progress: Math.min(prev.progress + 10, 90),
          }));
        }, 200);

        const metadata: DocumentMetadata = {
          name: formState.name.trim(),
          classification: formState.classification as DocumentClassification,
        };

        let uploadedDocument: UploadedDocument;

        if (uploadHandler) {
          // Use custom upload handler
          uploadedDocument = await uploadHandler(selectedFile, metadata);
        } else {
          // Default mock upload (for demo purposes)
          await new Promise((resolve) => setTimeout(resolve, 1500));
          uploadedDocument = {
            id: `doc-${Date.now()}`,
            name: metadata.name,
            classification: metadata.classification,
            size: selectedFile.size,
            mimeType: selectedFile.type,
            uploadedAt: new Date().toISOString(),
            blobUrl: URL.createObjectURL(selectedFile),
          };
        }

        clearInterval(progressInterval);
        setUploadState({ status: 'success', progress: 100, error: null });

        // Notify parent of success
        onUploadComplete(uploadedDocument);

        // Reset form after short delay to show success state
        setTimeout(() => {
          setSelectedFile(null);
        }, 1500);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Upload failed');
        setUploadState({
          status: 'error',
          progress: 0,
          error: error.message,
        });
        onUploadError(error);
      }
    },
    [
      validateForm,
      selectedFile,
      formState,
      uploadHandler,
      onUploadComplete,
      onUploadError,
    ]
  );

  /**
   * Handles keyboard events for accessibility
   */
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleBrowseClick();
      }
    },
    [handleBrowseClick]
  );

  // Determine drop zone classes
  const dropZoneClasses = [
    styles.dropZone,
    isDragOver && styles.dragOver,
    disabled && styles.disabled,
    uploadState.status === 'error' && styles.error,
    selectedFile && styles.hasFile,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={styles.documentUploader}>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedTypes.join(',')}
        onChange={handleInputChange}
        className={styles.hiddenInput}
        disabled={disabled || uploadState.status === 'uploading'}
        aria-hidden="true"
        tabIndex={-1}
      />

      {/* Drop Zone */}
      <div
        ref={dropZoneRef}
        className={dropZoneClasses}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={!selectedFile ? handleBrowseClick : undefined}
        onKeyDown={!selectedFile ? handleKeyDown : undefined}
        role={!selectedFile ? 'button' : undefined}
        tabIndex={!selectedFile && !disabled ? 0 : undefined}
        aria-label={!selectedFile ? 'Click or drag to upload a document' : undefined}
        aria-disabled={disabled}
      >
        {!selectedFile ? (
          // Empty state - show upload prompt
          <div className={styles.uploadPrompt}>
            <div className={styles.uploadIcon}>
              <ArrowUpload24Regular />
            </div>
            <div className={styles.uploadText}>
              <span className={styles.uploadTitle}>
                Drag and drop your document here
              </span>
              <span className={styles.uploadSubtitle}>
                or <button type="button" className={styles.browseLink} onClick={handleBrowseClick}>browse</button> to choose a file
              </span>
            </div>
            <div className={styles.uploadHint}>
              Accepted formats: {acceptedTypes.join(', ')} (max {maxSizeMB}MB)
            </div>
          </div>
        ) : (
          // File selected - show file info and form
          <div className={styles.fileSelected}>
            {/* File Info Header */}
            <div className={styles.fileHeader}>
              <div className={styles.fileInfo}>
                {getFileIcon(selectedFile.name)}
                <div className={styles.fileDetails}>
                  <span className={styles.fileName}>{selectedFile.name}</span>
                  <span className={styles.fileSize}>
                    {formatFileSize(selectedFile.size)}
                  </span>
                </div>
              </div>
              {uploadState.status !== 'uploading' && (
                <button
                  type="button"
                  className={styles.clearButton}
                  onClick={handleClearFile}
                  aria-label="Remove selected file"
                >
                  <Dismiss20Regular />
                </button>
              )}
            </div>

            {/* Upload Progress */}
            {uploadState.status === 'uploading' && (
              <div className={styles.progressSection}>
                <div className={styles.progressBar}>
                  <div
                    className={styles.progressFill}
                    style={{ width: `${uploadState.progress}%` }}
                  />
                </div>
                <span className={styles.progressText}>
                  Uploading... {uploadState.progress}%
                </span>
              </div>
            )}

            {/* Success State */}
            {uploadState.status === 'success' && (
              <div className={styles.successMessage}>
                <Checkmark20Regular className={styles.successIcon} />
                <span>Document uploaded successfully!</span>
              </div>
            )}

            {/* Error State */}
            {uploadState.status === 'error' && uploadState.error && (
              <div className={styles.errorMessage} role="alert" aria-live="assertive">
                <Warning20Regular className={styles.errorIcon} aria-hidden="true" />
                <span className={styles.errorText}>{uploadState.error}</span>
                <button
                  type="button"
                  className={styles.retryButton}
                  onClick={handleClearFile}
                  aria-label="Try uploading a different file"
                >
                  Try Again
                </button>
              </div>
            )}

            {/* Metadata Form */}
            {(uploadState.status === 'selected' ||
              uploadState.status === 'error') && (
              <form onSubmit={handleSubmit} className={styles.metadataForm}>
                {/* Document Name */}
                <div className={styles.formField}>
                  <label htmlFor="documentName" className={styles.label}>
                    Document Name <span className={styles.required}>*</span>
                  </label>
                  <input
                    id="documentName"
                    type="text"
                    className={`${styles.input} ${validationErrors.name ? styles.inputError : ''}`}
                    value={formState.name}
                    onChange={(e) => handleFormChange('name', e.target.value)}
                    placeholder="Enter document name"
                    disabled={disabled}
                    aria-required="true"
                    aria-invalid={!!validationErrors.name}
                    aria-describedby={
                      validationErrors.name ? 'name-error' : undefined
                    }
                  />
                  {validationErrors.name && (
                    <span id="name-error" className={styles.fieldError}>
                      {validationErrors.name}
                    </span>
                  )}
                </div>

                {/* Classification */}
                <div className={styles.formField}>
                  <label htmlFor="classification" className={styles.label}>
                    Classification <span className={styles.required}>*</span>
                  </label>
                  <select
                    id="classification"
                    className={`${styles.select} ${validationErrors.classification ? styles.inputError : ''}`}
                    value={formState.classification}
                    onChange={(e) =>
                      handleFormChange('classification', e.target.value)
                    }
                    disabled={disabled}
                    aria-required="true"
                    aria-invalid={!!validationErrors.classification}
                    aria-describedby={
                      validationErrors.classification
                        ? 'classification-error'
                        : undefined
                    }
                  >
                    <option value="">Select classification...</option>
                    {CLASSIFICATION_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {validationErrors.classification && (
                    <span id="classification-error" className={styles.fieldError}>
                      {validationErrors.classification}
                    </span>
                  )}
                </div>

                {/* File validation error */}
                {validationErrors.file && (
                  <div className={styles.errorMessage}>
                    <Warning20Regular className={styles.errorIcon} />
                    <span>{validationErrors.file}</span>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  className={styles.uploadButton}
                  disabled={disabled}
                >
                  <ArrowUpload24Regular className={styles.buttonIcon} />
                  Upload Document
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentUploader;
